import {
  FLOW_EFFECT_RPC_TIMEOUT_MS,
  type RobotCommandResult,
} from '@/connection/BatchRobotService';
import type { Logger, NodeDefinition } from '../../types';
import { t, r } from '../../rubyParts';
import {
  DEFAULT_SOUND_EFFECT,
  getSoundEffectLabel,
  isSoundEffect,
} from '../../config/soundEffects';
import { SoundEffectNodeView } from '../views/SoundEffectNode';
import { SoundEffectSettings } from '../settings/SoundEffectSettings';

const logRobotResults = (log: Logger, results: RobotCommandResult[]) => {
  results.forEach((result) => {
    if (result.status === 'fulfilled') {
      log(`[Sound Effect][${result.robotId}] 成功`);
      return;
    }
    log(`[Sound Effect][${result.robotId}] ${result.status}: ${result.message ?? result.errorCode ?? '不明なエラー'}`);
  });
};

export const soundEffectDefinition: NodeDefinition<'soundEffect'> = {
  label: 'Sound Effect',
  showInPalette: true,
  paletteColor: 'bg-orange-100 border-orange-500 text-orange-700',
  miniMapColor: '#f97316',
  defaultData: { label: 'Sound Effect', sound: DEFAULT_SOUND_EFFECT },
  view: SoundEffectNodeView,
  settings: SoundEffectSettings,
  run: async (node, ctx, log) => {
    const sound = node.data.sound ?? DEFAULT_SOUND_EFFECT;
    if (!isSoundEffect(sound)) {
      log('[Sound Effect] エラー: 効果音を選び直してください');
      return { nextHandle: null };
    }
    const label = getSoundEffectLabel(sound, 'ja');
    if (!ctx.robotService) {
      try {
        await ctx.playSimulationSound?.(sound, ctx.signal);
      } catch (error) {
        log(`[Sound Effect][Simulation] 再生エラー: ${error instanceof Error ? error.message : String(error)}`);
      }
      log(`[Sound Effect][Simulation] ${label}`);
      return;
    }

    const summary = await ctx.robotService.playSound(
      { sound },
      { nodeId: node.id, signal: ctx.signal, timeoutMs: FLOW_EFFECT_RPC_TIMEOUT_MS },
    );
    logRobotResults(log, summary.results);
    const succeeded = summary.results.filter((result) => result.status === 'fulfilled').length;
    log(`[Sound Effect] ${label}（${succeeded}/${summary.results.length}台）`);
  },
  help: {
    title: [t('Sound Effect（'), r('効果音', 'こうかおん'), t('）')],
    description: [
      r('選', 'えら'),
      t('んだ'),
      r('効果音', 'こうかおん'),
      t('を、ロボットから'),
      r('鳴', 'な'),
      t('らします。'),
    ],
  },
};
